'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Send, Smile, Flag } from 'lucide-react';
import type { Player, MatchPhase } from './MatchRoomContent';

interface ChatMessage {
  id: string;
  sender: 'p1' | 'p2' | 'system';
  text: string;
  timestamp: number;
  type?: 'taunt' | 'gg' | 'normal' | 'system';
}

const QUICK_MESSAGES = ['GG!', 'Good luck!', 'Well played!', 'Nice move!', '😎', '🔥', 'Let\'s go!', 'Rematch?'];

const INITIAL_MESSAGES: ChatMessage[] = [
  { id: 'sys-1', sender: 'system', text: 'Match room created. Fair play rules apply.', timestamp: Date.now() - 60000, type: 'system' },
  { id: 'opp-1', sender: 'p2', text: 'Good luck! May the best player win 🤝', timestamp: Date.now() - 30000, type: 'normal' },
];

const OPPONENT_RESPONSES = [
  'Nice one!', 'Good move!', 'Hmm, interesting...', 'GG!', 'You\'re good!',
  'Let\'s see what you\'ve got 😏', 'Well played!', 'Rematch after this?', '🔥🔥', 'Impressive!'
];

interface Props {
  self: Player;
  opponent: Player;
  opponentTyping: boolean;
  matchPhase: MatchPhase;
}

export default function MatchChat({ self, opponent, opponentTyping, matchPhase }: Props) {
  const [messages, setMessages] = useState<ChatMessage[]>(INITIAL_MESSAGES);
  const [input, setInput] = useState('');
  const [showQuick, setShowQuick] = useState(false);
  const [reportedSpam, setReportedSpam] = useState<Set<string>>(new Set());
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, opponentTyping]);

  // Simulate opponent responding occasionally
  useEffect(() => {
    if (messages.length < 2) return;
    const last = messages[messages.length - 1];
    if (last.sender !== 'p1') return;
    if (Math.random() > 0.5) return;
    const delay = 2000 + Math.random() * 3000;
    const t = setTimeout(() => {
      const text = OPPONENT_RESPONSES[Math.floor(Math.random() * OPPONENT_RESPONSES.length)];
      setMessages(m => [...m, { id: `opp-${Date.now()}`, sender: 'p2', text, timestamp: Date.now(), type: 'normal' }]);
    }, delay);
    return () => clearTimeout(t);
  }, [messages]);

  const sendMessage = (text: string) => {
    if (!text.trim()) return;
    // Basic profanity/spam filter
    const filtered = text.replace(/\b(spam|cheat|hack)\b/gi, '***');
    setMessages(m => [...m, { id: `p1-${Date.now()}`, sender: 'p1', text: filtered, timestamp: Date.now(), type: 'normal' }]);
    setInput('');
    setShowQuick(false);
  };

  const handleReport = (id: string) => {
    setReportedSpam(s => new Set([...s, id]));
    setMessages(m => [...m, { id: `sys-${Date.now()}`, sender: 'system', text: 'Message reported. Our moderation team will review it.', timestamp: Date.now(), type: 'system' }]);
  };

  const formatTime = (ts: number) => {
    const d = new Date(ts);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex flex-col flex-1 overflow-hidden" style={{ minHeight: 0 }}>
      <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
        <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--muted-foreground)' }}>Match Chat</span>
        <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.15)', color: '#10B981' }}>Live</span>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2" style={{ minHeight: 0 }}>
        {messages.map((msg) => {
          if (msg.type === 'system') {
            return (
              <div key={msg.id} className="text-center">
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--muted-foreground)' }}>
                  {msg.text}
                </span>
              </div>
            );
          }
          const isMe = msg.sender === 'p1';
          const isReported = reportedSpam.has(msg.id);
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'} group`}>
              <div className="max-w-[85%]">
                {!isMe && (
                  <p className="text-xs mb-0.5 font-semibold" style={{ color: '#F59E0B' }}>{opponent.name}</p>
                )}
                <div className="relative">
                  <div className={`px-3 py-2 rounded-2xl text-sm ${isMe ? 'rounded-tr-sm' : 'rounded-tl-sm'}`}
                    style={{
                      background: isMe ? 'linear-gradient(135deg, #7C3AED, #5B21B6)' : 'rgba(255,255,255,0.07)',
                      color: isReported ? 'var(--muted-foreground)' : 'var(--foreground)',
                      opacity: isReported ? 0.5 : 1,
                    }}>
                    {isReported ? '[Reported]' : msg.text}
                  </div>
                  {!isMe && !isReported && (
                    <button
                      onClick={() => handleReport(msg.id)}
                      className="absolute -right-5 top-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Report message"
                    >
                      <Flag size={10} style={{ color: 'var(--muted-foreground)' }} />
                    </button>
                  )}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)', textAlign: isMe ? 'right' : 'left' }}>
                  {formatTime(msg.timestamp)}
                </p>
              </div>
            </div>
          );
        })}
        {opponentTyping && (
          <div className="flex justify-start">
            <div className="px-3 py-2 rounded-2xl rounded-tl-sm" style={{ background: 'rgba(255,255,255,0.07)' }}>
              <div className="flex gap-1 items-center h-4">
                {[0, 1, 2].map(i => (
                  <span key={i} className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: 'var(--muted-foreground)', animationDelay: `${i * 150}ms` }} />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Quick messages */}
      {showQuick && (
        <div className="px-3 py-2 border-t flex flex-wrap gap-1.5" style={{ borderColor: 'var(--border)' }}>
          {QUICK_MESSAGES.map(q => (
            <button key={q} onClick={() => sendMessage(q)}
              className="text-xs px-2 py-1 rounded-full transition-all hover:scale-105"
              style={{ background: 'rgba(124,58,237,0.2)', color: '#A78BFA', border: '1px solid rgba(124,58,237,0.3)' }}>
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="px-3 py-2 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2 rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <button onClick={() => setShowQuick(s => !s)} className="flex-shrink-0 transition-colors hover:opacity-80">
            <Smile size={16} style={{ color: showQuick ? '#A78BFA' : 'var(--muted-foreground)' }} />
          </button>
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value.slice(0, 120))}
            onKeyDown={e => { if (e.key === 'Enter') sendMessage(input); }}
            placeholder="Say something…"
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: 'var(--foreground)' }}
          />
          <button onClick={() => sendMessage(input)} disabled={!input.trim()}
            className="flex-shrink-0 transition-all hover:scale-110 disabled:opacity-30">
            <Send size={15} style={{ color: '#7C3AED' }} />
          </button>
        </div>
        <p className="text-xs mt-1 text-center" style={{ color: 'var(--muted-foreground)', opacity: 0.5 }}>
          Be respectful · Rate limited · Moderated
        </p>
      </div>
    </div>
  );
}
